import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RiskLevel } from '~/types/RiskLevel';
import { useSelectRisk } from '~/controllers/agent';
import { useDirection } from '~/hooks/i18nHooks';
import { Select } from '~/components';
import { DirectionProp } from '~/types/Direction';
import direction from '~/utils/direction';
import colors from '~/constants/colors';
import { Tooltip } from 'antd';

const riskLevels: Record<string, RiskLevel> = {
  normal: 'NORMAL',
  highRisk: 'HIGH_RISK',
};

interface Props {
  currentValue?: RiskLevel;
  type: 'LEAD' | 'APPLICATION';
  recordId?: string;
  disabled?: boolean;
  onAfterChange?: () => void;
  splAddress?: boolean;
}

const RiskLevelSelect = (props: Props) => {
  const { currentValue, type, recordId, onAfterChange, disabled, splAddress } = props;
  const selectRisk = useSelectRisk();
  const dir = useDirection();
  const [t] = useTranslation();

  const options = Object.values(riskLevels).map((value) => ({
    label: t(value),
    value,
    key: value,
  }));

  const onSelect = (riskLevel: string) => {
    if (type === 'APPLICATION' && recordId) {
      selectRisk
        .mutateAsync({ applicationUid: recordId, level: riskLevel })
        .then(() => {
          onAfterChange?.();
        })
        .catch(() => { });
    }
  };

  if (!currentValue) return null;

  return (
    <Container dir={dir} tag={currentValue} disabled={disabled}>
      <Select
        id="riskLevelCell"
        onClick={(e) => {
          e.preventDefault();
        }}
        value={currentValue}
        disabled={disabled}
        options={options}
        size="small"
        onSelect={onSelect}
        dropdownMatchSelectWidth={false}
      />
      {splAddress && (
        <Tooltip title={t('National Address information is missing')}>
          <MissingAddressTag>
            {/* <ExclamationCircleOutlined className="warning-icon" /> */}
            <span>{t('SPL Missing')}</span>
          </MissingAddressTag>
        </Tooltip>
      )}
    </Container>
  );
};

const Container = styled.div<
  DirectionProp & {
    tag: RiskLevel;
    disabled?: boolean;
  }
>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;

  ${({ disabled }) =>
    disabled
      ? css`
          .ant-select-arrow {
            display: none;
          }
          .ant-select-selection-item {
            ${direction.padding.right('1px !important')};
            ${direction.padding.left('1px !important')};
          }
        `
      : css``}
  .ant-select-selection-item {
    font-weight: 500;
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector,
  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    box-shadow: 0 0 0;
  }

  ${({ tag }) =>
    tag === 'NORMAL'
      ? css`
          .ant-select-selection-item {
            color: ${colors.normalTagText} !important;
          }
          .ant-select-selector {
            border-color: ${colors.normalTagText} !important;
            background-color: ${colors.normalTagBackground} !important;
          }
        `
      : css`
          .ant-select-selection-item {
            color: ${colors.highRiskTagText} !important;
          }
          .ant-select-selector {
            border-color: ${colors.highRiskTagText} !important;
            background-color: ${colors.highRiskTagBackground} !important;
          }
        `}
`;

const MissingAddressTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 7px;
  padding: 4px 9px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: #d46b08;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(212, 107, 8, 0.04);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-variant:tabular-nums;

  .warning-icon {
    font-size: 11px;
    color: #fa8c16;
  }

  &:hover {
    background-color: #fff1b8;
    border-color: #ffd666;
    color: #b75904;
  }
`;

export default RiskLevelSelect;
